import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import { 
  MagnifyingGlassIcon, 
  PhotoIcon, 
  ArrowsRightLeftIcon,
  TagIcon,
  QrCodeIcon,
  ShareIcon,
  CloudArrowUpIcon,
  ChartBarIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  box-sizing: border-box;
`;

const Hero = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const Title = styled.h1`
  font-size: 4rem;
  color: ${({ theme }) => theme.colors.heading};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-family: 'Gagalin', sans-serif;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 3rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.8;
  max-width: 600px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  line-height: 1.6;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin: ${({ theme }) => theme.spacing.xxl} 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${({ theme }) => `linear-gradient(135deg, 
    ${theme.colors.white} 0%, 
    ${theme.colors.primary}05 100%)`};
  border-radius: 24px;
  padding: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    
    svg {
      transform: scale(1.1);
    }
  }
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  svg {
    width: 100%;
    height: 100%;
    transition: transform 0.3s ease;
  }
`;

const FeatureTitle = styled.h2`
  font-size: 1.75rem;
  color: ${({ theme }) => theme.colors.heading};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-family: 'Gagalin', sans-serif;
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureListItem = styled.li`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  padding-left: ${({ theme }) => theme.spacing.lg};
  position: relative;
  
  &::before {
    content: '→';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin: ${({ theme }) => theme.spacing.xxl} 0;
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  border-top: 1px solid ${({ theme }) => `${theme.colors.primary}15`};
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.heading};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-family: 'Gagalin', sans-serif;
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.8;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ColoredText = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-family: 'Gagalin', sans-serif;
`;

const GagalinText = styled.span`
  font-family: 'Gagalin', sans-serif;
  color: ${({ theme }) => theme.colors.heading};
`;

const Features = () => {
  return (
    <Container>
      <Hero>
        <Title>Powerful Features</Title>
        <Subtitle>
          Discover all the ways <ColoredText>BIN</ColoredText><GagalinText>VENTORY</GagalinText> can help you organize, track, and protect your belongings
        </Subtitle>
      </Hero>

      <FeatureGrid>
        <FeatureCard>
          <FeatureIcon>
            <MagnifyingGlassIcon />
          </FeatureIcon>
          <FeatureTitle>Smart Search</FeatureTitle>
          <FeatureDescription>
            Find any item instantly with our powerful search capabilities
          </FeatureDescription>
          <FeatureList>
            <FeatureListItem>Search across all rooms and bins</FeatureListItem>
            <FeatureListItem>Filter by tags, dates, and categories</FeatureListItem>
            <FeatureListItem>Save frequent searches</FeatureListItem>
            <FeatureListItem>Natural language processing</FeatureListItem>
          </FeatureList>
        </FeatureCard>

        <FeatureCard>
          <FeatureIcon>
            <PhotoIcon />
          </FeatureIcon>
          <FeatureTitle>Visual Records</FeatureTitle>
          <FeatureDescription>
            Keep detailed visual documentation of your belongings
          </FeatureDescription>
          <FeatureList>
            <FeatureListItem>Photos of each item</FeatureListItem>
            <FeatureListItem>Receipt storage</FeatureListItem>
            <FeatureListItem>Purchase history</FeatureListItem>
          </FeatureList>
        </FeatureCard>

        <FeatureCard>
          <FeatureIcon>
            <ArrowsRightLeftIcon />
          </FeatureIcon>
          <FeatureTitle>Easy Organization</FeatureTitle>
          <FeatureDescription>
            Organize your items exactly how you want them
          </FeatureDescription>
          <FeatureList>
            <FeatureListItem>Drag-and-drop interface</FeatureListItem>
            <FeatureListItem>Custom categories</FeatureListItem>
            <FeatureListItem>Bulk item moves</FeatureListItem>
            <FeatureListItem>Location history</FeatureListItem>
          </FeatureList>
        </FeatureCard>

        <FeatureCard>
          <FeatureIcon>
            <TagIcon />
          </FeatureIcon>
          <FeatureTitle>Smart Tagging</FeatureTitle>
          <FeatureDescription>
            Organize items with powerful tagging system
          </FeatureDescription>
          <FeatureList>
            <FeatureListItem>Custom tag creation</FeatureListItem>
            <FeatureListItem>Tag categories</FeatureListItem>
            <FeatureListItem>Bulk tagging</FeatureListItem>
            <FeatureListItem>Tag-based search</FeatureListItem>
          </FeatureList>
        </FeatureCard>

        <FeatureCard>
          <FeatureIcon>
            <QrCodeIcon />
          </FeatureIcon>
          <FeatureTitle>QR Labels</FeatureTitle>
          <FeatureDescription>
            Quick access to item information
          </FeatureDescription>
          <FeatureList>
            <FeatureListItem>Generate QR codes</FeatureListItem>
            <FeatureListItem>Print labels</FeatureListItem>
            <FeatureListItem>Scan with any device</FeatureListItem>
            <FeatureListItem>Custom label designs</FeatureListItem>
          </FeatureList>
        </FeatureCard>

        <FeatureCard>
          <FeatureIcon>
            <ShareIcon />
          </FeatureIcon>
          <FeatureTitle>Sharing</FeatureTitle>
          <FeatureDescription>
            Collaborate with family and friends
          </FeatureDescription>
          <FeatureList>
            <FeatureListItem>Share specific items or rooms</FeatureListItem>
            <FeatureListItem>Custom permissions</FeatureListItem>
            <FeatureListItem>Activity tracking</FeatureListItem>
            <FeatureListItem>Export capabilities</FeatureListItem>
          </FeatureList>
        </FeatureCard>
      </FeatureGrid>

      <CTASection>
        <CTATitle>Ready to Get Started?</CTATitle>
        {/* <CTAText>
          Join thousands of organized homeowners who trust BinVentory to keep track of their belongings
        </CTAText> */}
        <Button as={Link} to="/signup" variant="primary">
          Start for Free
        </Button>
      </CTASection>
    </Container>
  );
};

export default Features; 